import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  ArrowDownRight,
  ArrowUpRight,
  Warehouse,
  Package,
  History,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { saveToLocalDB, getFromLocalDB, addToSyncQueue } from '@/lib/offlineStorage';

const API = process.env.REACT_APP_BACKEND_URL;

const NASBodega = () => {
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [receiveForm, setReceiveForm] = useState({
    productId: '',
    quantity: 0,
    unit: 'sack',
    source: 'supplier',
    reference: '',
    notes: ''
  });
  
  const [transferForm, setTransferForm] = useState({
    productId: '',
    quantity: 0,
    destination: 'nas_shop',
    notes: ''
  });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    try {
      if (navigator.onLine) {
        // Load inventory
        const inventoryResponse = await axios.get(`${API}/api/nas/bodega/inventory`);
        setInventory(inventoryResponse.data);
        await saveToLocalDB('nas_bodega_inventory', inventoryResponse.data);
        
        // Load transactions
        const txResponse = await axios.get(`${API}/api/nas/bodega/transactions`);
        setTransactions(txResponse.data);
        await saveToLocalDB('nas_bodega_transactions', txResponse.data);
      } else {
        // Offline mode
        const localInventory = await getFromLocalDB('nas_bodega_inventory');
        const localTx = await getFromLocalDB('nas_bodega_transactions');
        if (localInventory) setInventory(localInventory);
        if (localTx) setTransactions(localTx);
        toast.info('Offline mode - showing cached data');
      }
    } catch (error) {
      console.error('Error loading bodega data:', error);
      // Fallback to local
      const localInventory = await getFromLocalDB('nas_bodega_inventory');
      const localTx = await getFromLocalDB('nas_bodega_transactions');
      if (localInventory) setInventory(localInventory);
      if (localTx) setTransactions(localTx);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleReceiveStock = async () => {
    if (!receiveForm.productId || receiveForm.quantity <= 0) {
      toast.error('Please select product and enter quantity');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        const params = new URLSearchParams({
          product_id: receiveForm.productId,
          quantity: receiveForm.quantity,
          source: receiveForm.source,
          reference: receiveForm.reference
        });
        await axios.post(`${API}/api/nas/bodega/receive?${params}`);
        toast.success(`Received ${receiveForm.quantity} units`);
        loadData(); // Refresh data
      } else {
        await addToSyncQueue({ 
          type: 'RECEIVE_STOCK', 
          data: receiveForm, 
          timestamp: new Date().toISOString() 
        });
        toast.success(`Stock received (will sync when online)`);
      }
      
      setReceiveForm({ productId: '', quantity: 0, unit: 'sack', source: 'supplier', reference: '', notes: '' });
      setIsReceiveModalOpen(false);
    } catch (error) {
      console.error('Error receiving stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to receive stock');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleTransferStock = async () => {
    if (!transferForm.productId || transferForm.quantity <= 0) {
      toast.error('Please select product and enter quantity');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        await axios.post(`${API}/api/nas/bodega/transfer`, {
          productId: transferForm.productId,
          quantity: transferForm.quantity,
          destination: transferForm.destination,
          notes: transferForm.notes
        });
        toast.success(`Transferred ${transferForm.quantity} units`);
        loadData(); // Refresh data
      } else {
        await addToSyncQueue({ 
          type: 'TRANSFER_STOCK', 
          data: transferForm, 
          timestamp: new Date().toISOString() 
        });
        toast.success(`Transfer queued (will sync when online)`);
      }
      
      setTransferForm({ productId: '', quantity: 0, destination: 'nas_shop', notes: '' });
      setIsTransferModalOpen(false);
    } catch (error) {
      console.error('Error transferring stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer stock');
    } finally {
      setIsSaving(false);
    }
  };
  
  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const todayTx = transactions.filter(t => 
    t.timestamp?.startsWith(new Date().toISOString().split('T')[0])
  );
  const todayReceived = todayTx.filter(t => t.type === 'receive').length;
  const todayTransferred = todayTx.filter(t => t.type === 'transfer').length;
  const totalStock = inventory.reduce((sum, i) => sum + (i.bodegaStock || 0), 0);
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bodega</h1>
          <p className="text-slate-500">Warehouse inventory management</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setIsReceiveModalOpen(true)} className="bg-green-600 hover:bg-green-700">
            <ArrowDownRight size={18} className="mr-2" />
            Receive Stock
          </Button>
          <Button onClick={() => setIsTransferModalOpen(true)} variant="outline">
            <ArrowUpRight size={18} className="mr-2" />
            Transfer Out
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Items</p>
                <p className="text-xl font-bold">{inventory.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Warehouse className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Stock</p>
                <p className="text-xl font-bold">{totalStock.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <ArrowDownRight className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Received Today</p>
                <p className="text-xl font-bold">{todayReceived}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <ArrowUpRight className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Transferred Today</p>
                <p className="text-xl font-bold">{todayTransferred}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {/* Inventory Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Stock */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Warehouse size={18} />
              Current Stock
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                </div>
              ) : filteredInventory.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No products in bodega</p>
              ) : (
                filteredInventory.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.name}</p>
                      <p className="text-sm text-slate-500">{item.category}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">{item.bodegaStock || 0}</p>
                        <p className="text-xs text-slate-500">in bodega</p>
                      </div>
                      {(item.bodegaStock || 0) > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTransferForm({
                              productId: item.id,
                              quantity: 0,
                              destination: 'nas_shop',
                              notes: ''
                            });
                            setIsTransferModalOpen(true);
                          }}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <ArrowUpRight size={14} className="mr-1" />
                          Transfer
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Recent Transactions */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History size={18} />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {transactions.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No recent activity</p>
              ) : (
                transactions.slice(0, 10).map(tx => (
                  <div key={tx.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      tx.type === 'receive' ? 'bg-green-100' : 'bg-purple-100'
                    }`}>
                      {tx.type === 'receive' ? (
                        <ArrowDownRight size={16} className="text-green-600" />
                      ) : (
                        <ArrowUpRight size={16} className="text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{tx.productName}</p>
                      <p className="text-xs text-slate-500">
                        {tx.type === 'receive' ? `From ${tx.source}` : `To ${tx.destination}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${tx.type === 'receive' ? 'text-green-600' : 'text-purple-600'}`}>
                        {tx.type === 'receive' ? '+' : '-'}{tx.quantity}
                      </p>
                      <p className="text-xs text-slate-500">{new Date(tx.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Receive Stock Modal */}
      <Dialog open={isReceiveModalOpen} onOpenChange={setIsReceiveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Product</Label>
              <Select value={receiveForm.productId} onValueChange={(v) => setReceiveForm({...receiveForm, productId: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {inventory.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={receiveForm.quantity || ''}
                  onChange={(e) => setReceiveForm({...receiveForm, quantity: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={receiveForm.unit} onValueChange={(v) => setReceiveForm({...receiveForm, unit: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sack">Sack</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="piece">Piece</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Source/Supplier</Label>
              <Input
                value={receiveForm.source}
                onChange={(e) => setReceiveForm({...receiveForm, source: e.target.value})}
                placeholder="e.g., ABC Feeds Corp"
              />
            </div>
            <div>
              <Label>Reference (Order #, DR #)</Label>
              <Input
                value={receiveForm.reference}
                onChange={(e) => setReceiveForm({...receiveForm, reference: e.target.value})}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReceiveModalOpen(false)}>Cancel</Button>
            <Button onClick={handleReceiveStock} disabled={isSaving} className="bg-green-600">
              {isSaving ? 'Receiving...' : 'Receive Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Transfer Stock Modal - Enhanced */}
      <Dialog open={isTransferModalOpen} onOpenChange={setIsTransferModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="text-purple-600" size={20} />
              Transfer Stock to NAS Shop
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Product Selection with Stock Info */}
            <div>
              <Label className="text-sm font-semibold">Select Product</Label>
              <Select value={transferForm.productId} onValueChange={(v) => setTransferForm({...transferForm, productId: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select product to transfer" />
                </SelectTrigger>
                <SelectContent>
                  {inventory.filter(p => (p.bodegaStock || 0) > 0).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{p.name}</span>
                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          {p.bodegaStock || 0} in stock
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Show selected product stock info */}
            {transferForm.productId && (
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">BODEGA STOCK</p>
                    <p className="text-2xl font-bold text-green-600">
                      {inventory.find(p => p.id === transferForm.productId)?.bodegaStock || 0}
                    </p>
                    <p className="text-xs text-slate-400">Available</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium">NAS SHOP STOCK</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {(inventory.find(p => p.id === transferForm.productId)?.shopStock?.wholesale || 0) +
                       (inventory.find(p => p.id === transferForm.productId)?.shopStock?.retail || 0)}
                    </p>
                    <p className="text-xs text-slate-400">Current</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Quick Transfer Buttons */}
            {transferForm.productId && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Quick Transfer</Label>
                <div className="flex gap-2 flex-wrap">
                  {[5, 10, 20, 50, 100].map(qty => {
                    const available = inventory.find(p => p.id === transferForm.productId)?.bodegaStock || 0;
                    return (
                      <Button
                        key={qty}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setTransferForm({...transferForm, quantity: Math.min(qty, available)})}
                        disabled={available < qty}
                        className={`${transferForm.quantity === qty ? 'bg-purple-100 border-purple-400' : ''}`}
                      >
                        {qty}
                      </Button>
                    );
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const available = inventory.find(p => p.id === transferForm.productId)?.bodegaStock || 0;
                      setTransferForm({...transferForm, quantity: available});
                    }}
                    className="bg-purple-50 text-purple-700 border-purple-300"
                  >
                    All
                  </Button>
                </div>
              </div>
            )}
            
            {/* Manual Quantity Input */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity to Transfer</Label>
                <Input
                  type="number"
                  value={transferForm.quantity || ''}
                  onChange={(e) => setTransferForm({...transferForm, quantity: parseFloat(e.target.value) || 0})}
                  className="text-lg font-semibold"
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Destination</Label>
                <Select value={transferForm.destination} onValueChange={(v) => setTransferForm({...transferForm, destination: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nas_shop">🏪 NAS Shop</SelectItem>
                    <SelectItem value="other_shop">🏬 Other Agrivet Shop</SelectItem>
                    <SelectItem value="farm">🚜 Farm Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* After Transfer Preview */}
            {transferForm.productId && transferForm.quantity > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>After Transfer:</strong> Bodega will have{' '}
                  <span className="font-bold">
                    {Math.max(0, (inventory.find(p => p.id === transferForm.productId)?.bodegaStock || 0) - transferForm.quantity)}
                  </span>{' '}
                  units remaining
                </p>
              </div>
            )}
            
            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={transferForm.notes}
                onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})}
                placeholder="e.g., For weekend stock"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTransferModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleTransferStock} 
              disabled={isSaving || !transferForm.productId || transferForm.quantity <= 0} 
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSaving ? 'Transferring...' : `Transfer ${transferForm.quantity || 0} to ${transferForm.destination === 'nas_shop' ? 'NAS Shop' : transferForm.destination}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASBodega;
